import { EventEmitter, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, tap } from "rxjs/operators";
import { MessageService } from "../messages/message.service";
import { environment } from "../../environments/environment";
import { SearchList } from "./search-list/search-list";
import { RichSearchList } from "./search-list/rich-search-list";
import { FiltersService } from "./search-list/filters/filters.service";
import { SearchQuery } from "./search-list/search-query-model";
import { Toast } from "../dialog/alert/toastr/toast";
import { ToastType } from "../dialog/alert/toastr/toast-type.enum";
import { AppConfigService } from "../config/app-config.service";
import { FulltextSearchList } from "./fulltext/fulltext-search-list";
import {ParamMap} from "@angular/router";
import { SearchRequest } from "./search-request.model";
import { SearchCacheService } from "./search-cache.service";
import { AuthService } from "../auth/auth.service";
import { UserCollectionsService } from "../auth/account/user-collections/user-collections.service";
import { FilterQueryConfig } from "../config/filters/filter-query-config.model";

@Injectable({
  providedIn: "root",
})
export class SearchService {
  searchList: RichSearchList;

  searchListEmitter = new EventEmitter();

  private baseUrl = "apiUrl";
  private fulltextBaseUrl = environment.fulltextUrl

  private maxResultWindow;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private filtersService: FiltersService,
    private configService: AppConfigService,
    private searchCacheService: SearchCacheService,
    private authService: AuthService,
    private userCollectionsService: UserCollectionsService
  ) {
    this.maxResultWindow = configService.maxResultWindow;
    this.searchList = new RichSearchList("", {}, this.maxResultWindow);
    this.authService.isLogged.subscribe((isLogged) => {
      if (isLogged) {
        this.userCollectionsService.favouritesCheck.next(this.searchList);
      } else {
        this.userCollectionsService.favouritesCheck.next(null);
      }
    });
    this.userCollectionsService.favouritesCheck.subscribe((searchList) => {
      if (searchList) {
        this.checkFavourites(searchList);
      } else {
        this.userCollectionsService.favourites.next([]);
      }
    });
  }

  markAsSearchInText(value: boolean) {
    this.filtersService.markAsSearchInText(value);
  }

  dumpSearchQuery() {
    return new SearchQuery({
      phrase: this.searchList.phrase,
      page: 0,
      sort: "creationTime:desc",
      pageSize: this.searchList.pageSize,
      filter: this.filtersService.filtersToString(),
      filterConfigs: this.filtersService.getAggregates(),
      fieldsToSearch: this.configService.fieldsToSearch,
    });
  }

  findAll(
    searchRequest: SearchRequest,
    facetConfig?: FilterQueryConfig[]
  ): Observable<RichSearchList> {
    this.filtersService.allPhraseFacets.next(searchRequest.phrase);

    const queryParams = new SearchQuery({
      phrase: searchRequest.phrase,
      page: this.limitPage(searchRequest.page, searchRequest.pageSize),
      pageSize: searchRequest.pageSize,
      sort: searchRequest.sort,
      filter: searchRequest.filter,
      filterConfigs: facetConfig
        ? facetConfig
        : this.filtersService.getAggregates(),
      fieldsToSearch: this.configService.fieldsToSearch,
      ids: searchRequest.ids,
      materialization: searchRequest.materialization,
    });
    const fulltextRequest = this.isFullTextRequest(searchRequest.textOnly);
    const resultCls = fulltextRequest ? FulltextSearchList : RichSearchList;

    if (fulltextRequest) {
      this.redirectToFulltext(searchRequest.phrase);
    }

    const url = `${this.baseUrl}/omnis/search/all`;
    let cachedRequest = this.searchCacheService.getFromCache(searchRequest);

    if (cachedRequest) {
      return of(
        this.invokeAfterEveryRequest(cachedRequest.searchRespone, searchRequest)
      );
    } else {
      const cachedRequestWithSameFacets =
        this.searchCacheService.getFromCacheSkipPage(searchRequest);
      if (cachedRequestWithSameFacets) {
        queryParams.skipFacets = true;
        cachedRequest = cachedRequestWithSameFacets;
      }
      this.messageService.clear();
      return this.http.post(url, queryParams).pipe(
        map(
          (data) =>
            new resultCls(searchRequest.phrase, data, this.maxResultWindow)
        ),
        tap((searchList) =>
          this.invokeAfterEveryRequest(searchList, searchRequest, cachedRequest)
        ),
        catchError(
          this.handleError(
            "findAll",
            new RichSearchList(searchRequest.phrase, {}, this.maxResultWindow)
          )
        )
      );
    }
  }

  private invokeAfterEveryRequest(
    searchList,
    searchRequest: SearchRequest,
    cachedRequest?: SearchRequest
  ) {
    if (cachedRequest && cachedRequest.searchRespone) {
      searchList.filters = cachedRequest.searchRespone.filters;
      searchList.facets = cachedRequest.searchRespone.facets;
    }
    if (searchList.isError()) {
      this.handleError("findAll", searchList)({ message: searchList.error });
    } else {
      this.searchList = searchList;
      this.fillCache(searchList, searchRequest);
    }
    this.filtersService.consumeFacets(searchList);
    this.userCollectionsService.favouritesCheck.next(searchList);
    this.searchListEmitter.emit();
    return searchList;
  }

  private fillCache(searchList: any, searchRequest: SearchRequest) {
    const fromCache = this.searchCacheService.getFromCache(searchRequest);
    if (!fromCache) {
      searchRequest.searchRespone = searchList;
      this.searchCacheService.putInCache(searchRequest);
    }
  }

  findFilters(
    phrase: string,
    facetValue: string,
    facetType: string,
    filter?: string,
    ids?: string[],
    config?: FilterQueryConfig[]
  ): Observable<any> {
    this.filtersService.allPhraseFacets.next(phrase);

    const queryParams = new SearchQuery({
      phrase: phrase,
      filter,
      filterConfigs: config,
      fieldsToSearch: this.configService.fieldsToSearch,
      ids,
    });

    queryParams.facetValue = facetValue;
    queryParams.facetType = facetType;

    const url = `${this.baseUrl}/omnis/search/facets`;
    this.messageService.clear();

    return this.http.post(url, queryParams);
  }

  findExpressions(val: number, page: number): Observable<SearchList> {
    const url = `${this.baseUrl}/omnis/search/expression/query/expr_work_id/${val}/${page}`;
    this.messageService.clear();

    return this.http.get(url).pipe(
      map((data) => new SearchList(data, this.maxResultWindow)),
      tap((searchList) => {
        if (searchList.isError()) {
          this.handleError(
            "findExpressions",
            searchList
          )({ message: searchList.error });
        }
      }),
      catchError(
        this.handleError(
          "findExpressions",
          new SearchList({}, this.maxResultWindow)
        )
      )
    );
  }

  findMaterializations(recId: number, page: number): Observable<SearchList> {
    const url = `${this.baseUrl}/omnis/search/materialization/query/${recId}/${page}`;
    this.messageService.clear();

    return this.http.get(url).pipe(
      map((data) => new SearchList(data, this.maxResultWindow)),
      tap((searchList) => {
        if (searchList.isError()) {
          this.handleError(
            "findMaterializations",
            searchList
          )({ message: searchList.error });
        }
      }),
      catchError(
        this.handleError(
          "findMaterializations",
          new SearchList({}, this.maxResultWindow)
        )
      )
    );
  }

  findObject(index: string, id: number): Observable<any> {
    const url = `${this.baseUrl}/omnis/search/${index}/${id}`;
    this.messageService.clear();

    return this.http
      .get<any>(url)
      .pipe(
        catchError(this.handleError<any>(`findObject index=${index}, id=${id}`))
      );
  }

  objectExists(index: string, id: string): Observable<boolean> {
    let url = null;
    if (index.includes("descriptor")) {
      url = `${this.baseUrl}/omnis/search/existsObjectById/${id}`;
    } else {
      url = `${this.baseUrl}/omnis/search/${index}/existsObjectById/${id}`;
    }
    return this.http
      .get<any>(url)
      .pipe(catchError(this.handleError<any>(`existsObject id=${id}`)));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.log(error);
      let msg = error.message;
      if (msg === "Http failure response for (unknown url): 0 Unknown Error") {
        msg = `Usługa '${this.baseUrl}' jest niedostępna.`;
      } else if (
        error &&
        error.error &&
        error.error.error === "all shards failed"
      ) {
        msg = "404";
      }
      this.messageService.add(new Toast("Błąd", msg, ToastType.ERROR));

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private limitPage(page: number, pageSize: number) {
    if (this.maxResultWindow < page * pageSize) {
      return Math.floor(this.maxResultWindow / pageSize);
    }
    return page;
  }

  public static prepareParamsObject(
    params: ParamMap,
    appConfigService: AppConfigService
  ): any {
    let sortField = params.get("sort");

    let phrase = params.get("query") || params.get("phrase");
    if (phrase == null) {
      phrase = "";
    }

    if (!sortField) {
      sortField = "_score:desc";
    }

    let page = +params.get("page");
    if (!page || page < 1) {
      page = 1;
    }

    let pageSize = +params.get("pageSize");
    if ((pageSize < 1 || pageSize > 1000) && appConfigService !== undefined) {
      pageSize = appConfigService.getConfig().defaultPageSize;
    }

    if (
      appConfigService !== undefined &&
      page * pageSize > appConfigService.maxResultWindow
    ) {
      page = Math.floor(appConfigService.maxResultWindow / pageSize);
    }

    let textOnly = params.get("searchInText") === "true";

    const filters = params.get("filters");

    const collectionId = params.get("collectionId");

    const queryId = params.get("queryId");

    return {
      phrase: phrase,
      sort: sortField,
      page: page,
      pageSize: pageSize,
      searchInText: textOnly,
      collectionId,
      filters,
      queryId,
    };
  }

  private isFullTextRequest(textOnly: boolean) {
    return textOnly != undefined && textOnly === true;
  }

  private checkFavourites(searchList: SearchList) {
    if (this.authService.isLogged.value && this.searchList) {
      this.authService.getToken().then((token) => {
        const itemsToCheck = [];
        if (searchList.search) {
          searchList.search.map((item) => {
            if (item && item["id"]) {
              itemsToCheck.push(item["id"]);
            }
          });
        }
        if (itemsToCheck.length > 0) {
          this.userCollectionsService
            .getFavourites(itemsToCheck, token)
            .subscribe((favs) => {
              this.userCollectionsService.favourites.next(favs);
            });
        }
      });
    }
  }

  redirectToFulltext(phrase: string) {
    window.location.href = `${this.fulltextBaseUrl}/results?query=${phrase}`;
  }
}
